import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {Autocomplete, Box, Button, Card, Chip, Grid, IconButton, MenuItem, Stack, TextField, Typography} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import {Field, FieldArray, useFormikContext} from 'formik';
import createRequisitionModel from './models/createRequisitionModel';
import {Delete as DeleteIcon} from '@mui/icons-material';
import {
  RequisitionItem,
  useGetBomByProjectByProjectIdQuery,
  useGetBomItemsQuery,
  useGetDevelopmentsByIdQuery,
  useGetDevelopmentsQuery,
  useGetItemsDropdownQuery,
  useGetProcurementProcessesByIdQuery,
  useGetUsersMeQuery,
  useGetUnitsByProjectByProjectIdQuery
} from '../../redux/vmsApi';
import DropzoneArea from '../common/DropzoneArea';
import {formatCurrency} from "../../utils/currencyUtil";
import {parseQuantity} from "../../utils/quantityUtil";


const {
  formField: {
    title,
    requestType,
    development,
    developmentName,
    projectName,
    project,
    totalCost,
    items: {
      fields: itemFields
    }
  },
} = createRequisitionModel;


const CreateRequisitionForm: React.FC = () => {
  const {errors, touched, values, setFieldValue} = useFormikContext<any>();
  const params = useParams();
  const [selectedDevelopment, setSelectedDevelopment] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [rowWarnings, setRowWarnings] = useState<Record<number, string>>({});
  const {data: currentUser} = useGetUsersMeQuery();
  const isTaskForceUser = currentUser?.roles?.includes('task-force') ?? false;

  const {data: developments = []} = useGetDevelopmentsQuery();
  const {data: inventoryItems = []} = useGetItemsDropdownQuery();
  const {data: bomsForProject = []} = useGetBomByProjectByProjectIdQuery(
    {projectId: selectedProject},
    {skip: !selectedProject},
  );
  const hasBomForSelectedProject = bomsForProject.length > 0;
  const {data: bomItems = []} = useGetBomItemsQuery(
    {
      filter: JSON.stringify({
        where: {projectId: selectedProject},
        order: ['itemDescription ASC'],
        limit: 1000,
      }),
    },
    {skip: !selectedProject || !hasBomForSelectedProject},
  );
  const {data: developmentData} = useGetDevelopmentsByIdQuery(
    {id: selectedDevelopment},
    {skip: !selectedDevelopment}
  );
  const {data: units = [], refetch: refetchUnits} = useGetUnitsByProjectByProjectIdQuery(
    {projectId: selectedProject},
    {skip: !selectedProject}
  );


  const handleDevelopmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const developmentId = event.target.value;
    setSelectedDevelopment(developmentId);
    setFieldValue('development', developmentId);
    setFieldValue('project', '');
    setSelectedProject('');
  };


  const handleUnitChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const unitId = event.target.value;

    const unitObject = units.find(unit => unit.id === unitId);
    const unitName = unitObject ? unitObject.unitName : '';

    const updatedItems = [...values.items];
    updatedItems[index] = {
      ...updatedItems[index],
      unit: unitId,
      unitName: unitName
    };
    setFieldValue('items', updatedItems);
  };


  const projects = selectedDevelopment
    ? developments.find((dev) => dev.id === selectedDevelopment)?.projects || []
    : [];

  const handleProjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const projectId = event.target.value;
    const selectedProjectObject = projects.find(project => project.id === projectId);
    setSelectedProject(projectId);
    setFieldValue('project', projectId);
    if (selectedProjectObject) {
      setFieldValue('projectName', selectedProjectObject.name);
    }
  };

  const unitOptions = [
    { value: 'm' },
    { value: 'm²' },
    { value: 'm³' },
    { value: 'kg' },
    { value: 'buckets' },
    { value: 'item'},
    { value: 'unit'},
    { value: 'length'},
    { value: 'bags' },
    { value: 'each' },
    { value: 'pack' },
    { value: 'box' },
    { value: 'litres' },
    { value: 'Tonnes' },
    { value: 'rolls' },
    { value: 'pairs' },
    { value: 'drums'},
    { value: 'hour(s)'},
    { value: 'day(s)'}
  ];

  const getBomKey = (itemCode: unknown, description: unknown) =>
    `${String(itemCode ?? '').trim().toLowerCase()}|${String(description ?? '').trim().toLowerCase()}`;

  const bomItemsByKey = new Map<string, any>();
  for (const item of bomItems as any[]) {
    bomItemsByKey.set(getBomKey(item.itemCode, item.itemDescription), item);
  }

  useEffect(() => {
    if (selectedProject) {
      refetchUnits();
    }
  }, [selectedProject, refetchUnits]);

  // const bomItems = boms.flatMap((bom: { sections: any[]; }) =>
  //   bom.sections.flatMap(section =>
  //     section.items.map((item: { itemId: any; description: any; quantity: any; quantityUnit: any; rate: any; totalCostPerItem: any }) => ({
  //       itemId: item.itemId,
  //       description: item.description,
  //       quantityValue: item.quantity || 0,
  //       quantityUnit: item.quantityUnit || '',
  //       rate: item.rate,
  //       totalCostPerItem: item.totalCostPerItem
  //     }))
  //   )
  // );

  const selectableItems = hasBomForSelectedProject
    ? bomItems
        .map((item: any) => ({
          itemCode: item.itemCode,
          description: item.itemDescription ?? item.itemItem ?? '',
          quantityUnit: item.itemUnit ?? '',
          rate: item.itemBudgetedCost ?? 0,
          availableQuantity: Number(item.itemQuantity ?? 0),
        }))
        .filter((item) => Number(item.availableQuantity ?? 0) > 0)
    : inventoryItems;


  const handleAutocompleteChange = (newValue: any | null, index: number) => {
    if (newValue) {
      if (hasBomForSelectedProject && Number(newValue.availableQuantity ?? 0) <= 0) {
        setRowWarnings(prev => ({
          ...prev,
          [index]: `${newValue.description || 'Selected item'} is depleted in BOM (available quantity is 0).`,
        }));
        setFieldValue(`items[${index}].itemCode`, '');
        setFieldValue(`items[${index}].description`, '');
        setFieldValue(`items[${index}].quantity.quantityValue`, '');
        setFieldValue(`items[${index}].quantity.quantityUnit`, '');
        setFieldValue(`items[${index}].rate`, 0);
        setFieldValue(`items[${index}].totalCostPerItem`, 0);
        return;
      }

      setRowWarnings(prev => ({ ...prev, [index]: '' }));
      setFieldValue(`items[${index}].itemCode`, newValue.itemCode);
      setFieldValue(`items[${index}].description`, newValue.description);
      setFieldValue(`items[${index}].quantity.quantityValue`, 0);
      setFieldValue(`items[${index}].quantity.quantityUnit`, newValue.quantityUnit);
      setFieldValue(`items[${index}].rate`, parseQuantity(newValue.rate ?? 0));
      setFieldValue(`items[${index}].totalCostPerItem`, 0);

    } else {
      setRowWarnings(prev => ({ ...prev, [index]: '' }));
      setFieldValue(`items[${index}].itemCode`, '');
      setFieldValue(`items[${index}].description`, '');
      setFieldValue(`items[${index}].quantity.quantityValue`, '');
      setFieldValue(`items[${index}].quantity.quantityUnit`, '');
      setFieldValue(`items[${index}].rate`, 0);
    }
  };

  const {data: procurementProcess} = useGetProcurementProcessesByIdQuery({id: params?.uuid ?? ""}, {skip: !params?.uuid});

  useEffect(() => {
    if (procurementProcess) {
      const {requisition} = procurementProcess;
      if (requisition) {
        setFieldValue('title', requisition.title);
        setFieldValue('requestType', procurementProcess.requestType || 'PROCUREMENT');
        setFieldValue(`development`, requisition.development.id);
        setSelectedDevelopment(requisition.development.id);
        setFieldValue(`project`, requisition.project.id);
        setFieldValue('priority', procurementProcess.priority || 'Standard');
        setFieldValue(`documents`, requisition.documents);
        setSelectedProject(requisition.project.id);
        requisition.items.forEach((item, index) => {
          setFieldValue(`items[${index}].itemCode`, item.itemCode);
          setFieldValue(`items[${index}].description`, item.description);
          setFieldValue(`items[${index}].quantity.quantityValue`, item.quantity?.quantityValue);
          setFieldValue(`items[${index}].quantity.quantityUnit`, item.quantity.quantityUnit);
          setFieldValue(`items[${index}].unit`, item.unit?.id);
          setFieldValue(`items[${index}].unitName`, item.unit?.name);
          setFieldValue(`items[${index}].rate`, 0);
          setFieldValue(`items[${index}].totalCostPerItem`, item.totalCostPerItem);
        });
      }

    }
  }, [procurementProcess, setFieldValue]);

  useEffect(() => {
    if (!values.priority) {
      setFieldValue('priority', 'Standard', false);
    }
  }, [values.priority, setFieldValue]);

  useEffect(() => {
    if (developmentData) {
      setFieldValue('developmentName', developmentData.name);
    }
  }, [developmentData, setFieldValue]);

  useEffect(() => {
    const total = calculateTotal(values.items);

    if (values.totalCost !== total) {
      setFieldValue('totalCost', total);
    }
  }, [values.items, values.totalCost, setFieldValue]);

  useEffect(() => {
    if (!hasBomForSelectedProject) {
      if ((values as any).bomSubmissionBlocked) {
        setFieldValue('bomSubmissionBlocked', false, false);
      }
      if ((values as any).bomSubmissionMessage) {
        setFieldValue('bomSubmissionMessage', '', false);
      }
      return;
    }

    let message = '';
    const activeItems = (values.items || []).filter((item: any) =>
      String(item?.itemCode ?? '').trim() ||
      String(item?.description ?? '').trim() ||
      parseQuantity(item?.quantity?.quantityValue) > 0,
    );

    for (const item of activeItems) {
      const key = getBomKey(item?.itemCode, item?.description);
      const matchedBom = bomItemsByKey.get(key);

      if (!matchedBom) {
        message = 'This project has BOM. Select requisition items from BOM only.';
        break;
      }

      const availableQty = Number(matchedBom.itemQuantity ?? 0);
      const requestedQty = parseQuantity(item?.quantity?.quantityValue);

      if (availableQty <= 0) {
        message = `BOM item ${matchedBom.itemCode} (${matchedBom.itemDescription}) is depleted and cannot be requested.`;
        break;
      }

      if (requestedQty > availableQty) {
        message = `Requested quantity for ${matchedBom.itemCode} exceeds BOM balance. Available: ${availableQty}, requested: ${requestedQty}.`;
        break;
      }
    }

    const blocked = Boolean(message);
    if (Boolean((values as any).bomSubmissionBlocked) !== blocked) {
      setFieldValue('bomSubmissionBlocked', blocked, false);
    }
    if (((values as any).bomSubmissionMessage || '') !== message) {
      setFieldValue('bomSubmissionMessage', message, false);
    }
  }, [
    values.items,
    values.bomSubmissionBlocked,
    values.bomSubmissionMessage,
    hasBomForSelectedProject,
    setFieldValue,
    selectedProject,
    bomItems,
  ]);

  function calculateTotal(items: any): number {
    return items.reduce((acc: number, item: any) => {
      const totalPerItem = parseQuantity(item.quantity.quantityValue) * parseQuantity(item.rate) || 0;
      return acc + totalPerItem;
    }, 0);
  }

  return (
    <Stack width="100%" spacing={5}>


      <Stack width="100%" spacing={4} padding={3}>
        <Card elevation={3} style={{padding: '20px', backgroundColor: '#f9f9f9'}}>
          <Typography variant="h5" gutterBottom style={{fontWeight: 'bold', color: '#123456'}}>
            {procurementProcess?.requisition ? 'Correct Requisition' : 'Raise Requisition'}
          </Typography>

          {isTaskForceUser && (
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Typography variant="body2" sx={{ color: '#555', fontWeight: 600 }}>
                Priority:
              </Typography>
              <Chip
                icon={<PriorityHighIcon />}
                label="Urgent"
                clickable
                onClick={() =>
                  setFieldValue('priority', values.priority === 'Urgent' ? 'Standard' : 'Urgent')
                }
                sx={{
                  backgroundColor: values.priority === 'Urgent' ? '#FF8C00' : '#fff',
                  color: values.priority === 'Urgent' ? '#fff' : '#FF8C00',
                  border: '2px solid #FF8C00',
                  fontWeight: 'bold',
                  '& .MuiChip-icon': { color: values.priority === 'Urgent' ? '#fff' : '#FF8C00' },
                  transition: 'all 0.2s',
                }}
              />
              <Chip
                icon={<ErrorOutlineIcon />}
                label="Critical"
                clickable
                onClick={() =>
                  setFieldValue('priority', values.priority === 'Critical' ? 'Standard' : 'Critical')
                }
                sx={{
                  backgroundColor: values.priority === 'Critical' ? '#D32F2F' : '#fff',
                  color: values.priority === 'Critical' ? '#fff' : '#D32F2F',
                  border: '2px solid #D32F2F',
                  fontWeight: 'bold',
                  '& .MuiChip-icon': { color: values.priority === 'Critical' ? '#fff' : '#D32F2F' },
                  transition: 'all 0.2s',
                }}
              />
              {values.priority && values.priority !== 'Standard' && (
                <Typography variant="caption" sx={{ color: '#888', fontStyle: 'italic' }}>
                  {values.priority === 'Urgent' ? 'Marked as Urgent' : 'Marked as Critical'}
                </Typography>
              )}
            </Box>
          )}
          <Field name="priority" type="hidden" />

          <br></br>
          <Stack direction="row" spacing={2}>
            <Field
              as={TextField}
              fullWidth
              {...title}
              label="Title"
              sx={{backgroundColor: '#fff', borderRadius: 1}}
              error={touched[title.name] && Boolean(errors[title.name])}
              helperText={touched[title.name] && errors[title.name] ? errors[title.name] : ''}
            />

            <Field
              as={TextField}
              fullWidth
              select
              {...requestType}
              value={values.requestType || 'PROCUREMENT'}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setFieldValue('requestType', event.target.value)
              }
              sx={{backgroundColor: '#fff', borderRadius: 1}}
              error={touched[requestType.name] && Boolean(errors[requestType.name])}
              helperText={touched[requestType.name] && errors[requestType.name] ? errors[requestType.name] : ''}
            >
              <MenuItem value="PROCUREMENT">PROCUREMENT</MenuItem>
              <MenuItem value="FUNDS REQUEST">FUNDS REQUEST</MenuItem>
            </Field>

            <Field
              as={TextField}
              fullWidth
              select

              {...development}
              value={values.development || ''}
              onChange={handleDevelopmentChange}
              sx={{backgroundColor: '#fff', borderRadius: 1}}
              error={touched[development.name] && Boolean(errors[development.name])}
              helperText={touched[development.name] && errors[development.name] ? errors[development.name] : ''}
            >
              {developments.map((development) => (
                <MenuItem key={development.id} value={development.id}>
                  {development.name}
                </MenuItem>
              ))}
            </Field>

            <Field {...developmentName} type="hidden"/>
            <Field {...projectName} type="hidden"/>

            <Field
              as={TextField}
              fullWidth
              select

              {...project}
              value={values.project || ''}
              onChange={handleProjectChange}
              sx={{backgroundColor: '#fff', borderRadius: 1}}
              error={touched[project.name] && Boolean(errors[project.name])}
              helperText={touched[project.name] && errors[project.name] ? errors[project.name] : ''}
            >
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Field>

            <Field {...totalCost} type="hidden"/>
          </Stack>

          <FieldArray name="items">
            {({push, remove}) => (
              <>
                {values.items.map((item: RequisitionItem, index: number) => (
                  <Stack direction="row" spacing={2} alignItems="center" key={index} sx={{mt: 2}}>
                    <Field
                      as={TextField}
                      style={{width: '200px'}}
                      select
                      value={values.items[index]?.unit || ''}
                      {...itemFields.unit}
                      onChange={(event: any) => handleUnitChange(event, index)}
                      sx={{backgroundColor: '#fff', borderRadius: 1}}
                      error={touched[`items[${index}].unit.name`] && Boolean(errors[`items[${index}].unit`])}
                      helperText={touched[`items[${index}].unit.name`] && errors[`items[${index}].unit.name`] ? String(errors[`items[${index}].unit.name`]) : ''}
                    >
                      {units.map((unit) => (
                        <MenuItem key={unit.id} value={unit.id}>
                          {unit.unitName}
                        </MenuItem>
                      ))}
                    </Field>

                    <Field {...itemFields.unitName} type="hidden"/>

                    {/* <Autocomplete
                      options={bomItems}
                      getOptionLabel={(option) => option.description}
                      onChange={(event, newValue) => handleAutocompleteChange(newValue, index)}
                      style={{ width: '600px' }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search BOM Item"
                          variant="outlined"
                          sx={{ backgroundColor: '#fff', borderRadius: 1 }}
                          error={touched.selectedBomItem && Boolean(errors.selectedBomItem)}
                          helperText={touched.selectedBomItem && errors.selectedBomItem ? errors.selectedBomItem : ''}
                        />
                      )}
                    /> */}

                    <Autocomplete
                      options={selectableItems as any[]}
                      getOptionLabel={(option) => option.description || ''}
                      onChange={(event, newValue) => handleAutocompleteChange(newValue, index)}
                      getOptionDisabled={(option: any) =>
                        hasBomForSelectedProject && Number(option.availableQuantity ?? 0) <= 0
                      }
                      style={{width: '600px'}}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={hasBomForSelectedProject ? 'Search BOM Item' : 'Search Inventory Item'}
                          variant="outlined"
                          sx={{backgroundColor: '#fff', borderRadius: 1}}
                          helperText={rowWarnings[index] || ''}
                          error={Boolean(rowWarnings[index])}
                        />
                      )}
                    />

                    <Field {...itemFields.itemCode} type="hidden"/>
                    <TextField
                      {...itemFields.description}
                      label="Description"
                      variant="outlined"
                      value={values.items[index]?.description}
                      style={{width: '600px', backgroundColor: '#fff', borderRadius: 1}}


                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFieldValue(`items[${index}].description`, e.target.value)
                      }
                      disabled={hasBomForSelectedProject}
                      helperText={
                        hasBomForSelectedProject
                          ? 'Description is controlled by BOM selection for this project.'
                          : touched[`items[${index}].description.name`] && errors[`items[${index}].description.name`]
                          ? String(errors[`items[${index}].description.name`])
                          : ''
                      }

                      error={touched[`items[${index}].description.name`] && Boolean(errors[`items[${index}].description.name`])}
                    />
                    <TextField
                      {...itemFields.quantity.quantityValue}
                      type="text"
                      label="Quantity"
                      variant="outlined"
                      value={values.items[index]?.quantity?.quantityValue}
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        if (hasBomForSelectedProject) {
                          const key = getBomKey(values.items[index]?.itemCode, values.items[index]?.description);
                          const matchedBom = bomItemsByKey.get(key);
                          const availableQty = Number(matchedBom?.itemQuantity ?? 0);
                          const requestedQty = parseQuantity(nextValue);

                          if (matchedBom && requestedQty > availableQty) {
                            setRowWarnings(prev => ({
                              ...prev,
                              [index]: `Requested quantity exceeds BOM balance. Available: ${availableQty}.`,
                            }));
                            setFieldValue(`items[${index}].quantity.quantityValue`, availableQty);
                            return;
                          }
                        }

                        setRowWarnings(prev => ({ ...prev, [index]: '' }));
                        setFieldValue(`items[${index}].quantity.quantityValue`, nextValue);
                      }}
                      sx={{backgroundColor: '#fff', borderRadius: 1}}
                      inputProps={{ inputMode: 'decimal' }}
                    />

                    <Field
                      as={TextField}

                      select

                      value={values.items[index]?.quantity?.quantityUnit || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFieldValue(`items[${index}].quantity.quantityUnit`, e.target.value)
                      }
                      {...itemFields.quantity.quantityUnit}
                      error={touched[`items[${index}].quantity.quantityUnit.name`] && Boolean(errors[`items[${index}].quantity.quantityUnit.name`])}
                      helperText={touched[`items[${index}].quantity.quantityUnit.name`] && errors[`items[${index}].quantity.quantityUnit.name`] ? String(errors[`items[${index}].quantity.quantityUnit.name`]) : ''}
                      sx={{minWidth: '100px', backgroundColor: '#fff', borderRadius: 1}}

                    >
                      {unitOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.value}
                        </MenuItem>
                      ))}
                    </Field>

                    <TextField
                      {...itemFields.rate}
                      label="Rate"
                      type="number"
                      variant="outlined"
                      value={values.items[index]?.rate || 0}
                      sx={{backgroundColor: '#fff', borderRadius: 1}}
                      disabled={hasBomForSelectedProject}
                      onChange={(e) => {
                        const rate = e.target.value;
                        const quantityValue = values.items[index]?.quantity?.quantityValue || 0;
                        setFieldValue(`items[${index}].rate`, rate);
                        const totalCost = parseQuantity(rate) * parseQuantity(quantityValue);
                        setFieldValue(`items[${index}].totalCostPerItem`, totalCost);
                      }}
                    />

                    <TextField
                      {...itemFields.totalCostPerItem}
                      label="Total Amount"
                      value={formatCurrency(parseQuantity(item.quantity.quantityValue) * parseQuantity(item.rate) || 0)}
                      variant="outlined"
                      sx={{backgroundColor: '#fff', borderRadius: 1}}
                      disabled
                    />


                    <IconButton color="error" onClick={() => remove(index)}>
                      <DeleteIcon/>
                    </IconButton>
                  </Stack>
                ))}

                <Button
                  onClick={() => push({description: '', quantity: {quantityValue: '', quantityUnit: ''}, unit: ''})}
                  variant="contained"
                  color="primary"
                  size="small"
                  sx={{mt: 2}}
                >
                  Add Requisition Item
                </Button>
              </>
            )}
          </FieldArray>
          <br></br>
          <br></br>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{color: 'textSecondary'}}>
              Attachments
            </Typography>
            <DropzoneArea
              setFieldValue={setFieldValue}
              fieldName="documents"
              attachments={values.documents || []}
            />
          </Grid>

          <Card
            sx={{
              backgroundColor: '#d1e7dd',
              color: '#0f5132',
              padding: '15px',
              marginTop: '20px',
              textAlign: 'right',
              fontWeight: 'bold',
              fontSize: '1.2em',
            }}
          >
            Total: {formatCurrency(values.totalCost || 0)}
          </Card>

          {Boolean((values as any).bomSubmissionBlocked) && (
            <Typography sx={{ mt: 2, color: '#b42318', fontWeight: 600 }}>
              {(values as any).bomSubmissionMessage}
            </Typography>
          )}
        </Card>
      </Stack>


    </Stack>
  );
};

export default CreateRequisitionForm;
